import { useCallback, useEffect, useState } from 'react';

import { useCurrentUserStore } from 'store/currentUser';
import type { IUser } from 'store/declare';
import { useUsersStore } from 'store/users';

import { useAllUserList } from 'hooks/useAllUserList';

import { throttleFunction } from 'utils/throttle';

import { useActiveUserQueue } from './useActiveUserQueue';

// кол-во миллисекунд через которое возможно обновление активного пользователя
// используется для того, чтобы избежать частого обновления
const throttleAmount = 2000;

/** Хук для определения активного пользователя */
export function useActiveUser() {
    const { currentUser } = useCurrentUserStore();
    const { users } = useUsersStore();
    const { screenShareUser } = useAllUserList();

    // пользователь, данные которого будут отображаться в центральной части приложения
    const [activeUser, setActiveUser] = useState<IUser | null>(null);
    const { getFirstInQueue, updateQueue } = useActiveUserQueue();

    const setActiveUserFunction = useCallback(() => {
        const newActiveUser = users.find(user => user.id === getFirstInQueue());

        if (newActiveUser) setActiveUser(newActiveUser);
    }, [users, getFirstInQueue]);

    const throttledFunction = useCallback(throttleFunction(setActiveUserFunction, throttleAmount), [setActiveUserFunction]);

    // управление очередью активных пользователей
    useEffect(() => {
        updateQueue();
        throttledFunction();
    }, [updateQueue, throttledFunction]);

    useEffect(() => {
        // в конференции есть только текущий пользователь
        const isOnlyCurrentUser = !users.length;

        if (isOnlyCurrentUser) setActiveUser(currentUser);
    }, [users, currentUser]);

    // если есть пользователь демонстрирующий экран - делаем такого пользователя активным
    useEffect(() => {
        if (screenShareUser) setActiveUser(screenShareUser);
    }, [screenShareUser]);

    return activeUser;
}
